import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Pill, Plus, Search, Edit, Trash2, Loader2, AlertTriangle, Package, TrendingDown, Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const CATEGORIAS = [
  { value: "analgesico", label: "Analgésico" },
  { value: "antibiotico", label: "Antibiótico" },
  { value: "antiinflamatorio", label: "Antiinflamatorio" },
  { value: "antialergico", label: "Antialérgico" },
  { value: "vitaminas", label: "Vitaminas" },
  { value: "gastrointestinal", label: "Gastrointestinal" },
  { value: "cardiovascular", label: "Cardiovascular" },
  { value: "respiratorio", label: "Respiratorio" },
  { value: "otros", label: "Otros" }
];

const PRESENTACIONES = ["Tabletas", "Cápsulas", "Jarabe", "Inyectable", "Crema", "Gotas", "Suspensión", "Sobres"];

export default function MedicamentosPage() {
  const [medicamentos, setMedicamentos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("all");
  const [filterBajoStock, setFilterBajoStock] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  const [formData, setFormData] = useState({
    codigo: "", nombre: "", descripcion: "", principio_activo: "", presentacion: "",
    concentracion: "", laboratorio: "", categoria: "", requiere_receta: false,
    temperatura_almacenamiento: "ambiente", stock_minimo: 10, stock_actual: 0,
    unidad_medida: "unidades", precio_unitario: 0, ubicacion: "", notas: ""
  });
  
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [medsRes, statsRes] = await Promise.all([
        axios.get(`${API}/medicamentos`, {
          ...getAuthHeaders(),
          params: { skip: page * limit, limit, search: searchTerm || undefined, categoria: filterCategoria !== "all" ? filterCategoria : undefined, bajo_stock: filterBajoStock }
        }),
        axios.get(`${API}/medicamentos/dashboard/stats`, getAuthHeaders())
      ]);
      setMedicamentos(medsRes.data.items || []);
      setTotal(medsRes.data.total || 0);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, searchTerm, filterCategoria, filterBajoStock]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditing(null);
    setFormData({ codigo: "", nombre: "", descripcion: "", principio_activo: "", presentacion: "", concentracion: "", laboratorio: "", categoria: "", requiere_receta: false, temperatura_almacenamiento: "ambiente", stock_minimo: 10, stock_actual: 0, unidad_medida: "unidades", precio_unitario: 0, ubicacion: "", notas: "" });
    setShowDialog(true);
  };

  const openEdit = (med) => {
    setEditing(med);
    setFormData({ ...med });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.codigo || !formData.nombre) { toast.error("Código y nombre requeridos"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/medicamentos/${editing.id}`, formData, getAuthHeaders());
        toast.success("Medicamento actualizado");
      } else {
        await axios.post(`${API}/medicamentos`, formData, getAuthHeaders());
        toast.success("Medicamento creado");
      }
      setShowDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar medicamento?")) return;
    try {
      await axios.delete(`${API}/medicamentos/${id}`, getAuthHeaders());
      toast.success("Eliminado"); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="medicamentos-page">
        <div className="flex justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold dark:text-white">Control de Medicamentos</h1>
            <p className="text-slate-500">{total} medicamentos registrados</p>
          </div>
          <Button onClick={openNew} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"><Plus className="w-4 h-4 mr-2" />Nuevo Medicamento</Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4 flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-500" />
              <div><p className="text-2xl font-bold text-blue-700">{stats?.total_medicamentos || 0}</p><p className="text-sm text-blue-600">Total</p></div>
            </CardContent>
          </Card>
          <Card className={`border-amber-200 ${stats?.bajo_stock > 0 ? 'bg-amber-50' : 'bg-slate-50'}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <TrendingDown className="w-8 h-8 text-amber-500" />
              <div><p className="text-2xl font-bold text-amber-700">{stats?.bajo_stock || 0}</p><p className="text-sm text-amber-600">Bajo Stock</p></div>
            </CardContent>
          </Card>
          <Card className={`border-red-200 ${stats?.lotes_por_vencer > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <Calendar className="w-8 h-8 text-red-500" />
              <div><p className="text-2xl font-bold text-red-700">{stats?.lotes_por_vencer || 0}</p><p className="text-sm text-red-600">Por Vencer</p></div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <p className="text-sm text-green-600">Valor Total</p>
              <p className="text-xl font-bold text-green-700">RD$ {(stats?.valor_total || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }} className="pl-10" />
              </div>
              <Select value={filterCategoria} onValueChange={(v) => { setFilterCategoria(v); setPage(0); }}>
                <SelectTrigger><SelectValue placeholder="Categoría" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {CATEGORIAS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Switch checked={filterBajoStock} onCheckedChange={(v) => { setFilterBajoStock(v); setPage(0); }} />
                <Label>Solo bajo stock</Label>
              </div>
              <Button variant="ghost" onClick={() => { setSearchTerm(""); setFilterCategoria("all"); setFilterBajoStock(false); setPage(0); }}>Limpiar</Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
            ) : (
              <>
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50 dark:bg-slate-800">
                      <th className="text-left p-4 font-medium text-slate-600">Código</th>
                      <th className="text-left p-4 font-medium text-slate-600">Medicamento</th>
                      <th className="text-left p-4 font-medium text-slate-600">Categoría</th>
                      <th className="text-left p-4 font-medium text-slate-600">Stock</th>
                      <th className="text-left p-4 font-medium text-slate-600">Precio</th>
                      <th className="text-right p-4 font-medium text-slate-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medicamentos.length === 0 ? (
                      <tr><td colSpan="6" className="p-8 text-center text-slate-500">No se encontraron medicamentos</td></tr>
                    ) : medicamentos.map(med => (
                      <tr key={med.id} className="border-b hover:bg-slate-50">
                        <td className="p-4 font-mono text-sm">{med.codigo}</td>
                        <td className="p-4">
                          <p className="font-medium dark:text-white">{med.nombre}</p>
                          <p className="text-sm text-slate-500">{med.presentacion} {med.concentracion}</p>
                        </td>
                        <td className="p-4"><Badge variant="outline">{CATEGORIAS.find(c => c.value === med.categoria)?.label || med.categoria}</Badge></td>
                        <td className="p-4">
                          <span className={med.stock_actual <= med.stock_minimo ? "text-red-600 font-bold" : ""}>{med.stock_actual}</span>
                          <span className="text-slate-400 text-sm"> / {med.stock_minimo} mín</span>
                          {med.stock_actual <= med.stock_minimo && <AlertTriangle className="w-4 h-4 text-amber-500 inline ml-2" />}
                        </td>
                        <td className="p-4">RD$ {med.precio_unitario?.toLocaleString()}</td>
                        <td className="p-4 text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(med)}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(med.id)}><Trash2 className="w-4 h-4" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t">
                    <p className="text-sm text-slate-500">Mostrando {page * limit + 1}-{Math.min((page + 1) * limit, total)} de {total}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                      <span className="px-3 py-1">{page + 1} / {totalPages}</span>
                      <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle><Pill className="w-5 h-5 inline mr-2" />{editing ? "Editar" : "Nuevo"} Medicamento</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div><Label>Código *</Label><Input value={formData.codigo} onChange={e => setFormData({...formData, codigo: e.target.value})} /></div>
              <div><Label>Nombre *</Label><Input value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} /></div>
              <div><Label>Principio Activo</Label><Input value={formData.principio_activo} onChange={e => setFormData({...formData, principio_activo: e.target.value})} /></div>
              <div><Label>Laboratorio</Label><Input value={formData.laboratorio} onChange={e => setFormData({...formData, laboratorio: e.target.value})} /></div>
              <div><Label>Presentación</Label>
                <Select value={formData.presentacion} onValueChange={v => setFormData({...formData, presentacion: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{PRESENTACIONES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Concentración</Label><Input value={formData.concentracion} onChange={e => setFormData({...formData, concentracion: e.target.value})} placeholder="500mg" /></div>
              <div><Label>Categoría</Label>
                <Select value={formData.categoria} onValueChange={v => setFormData({...formData, categoria: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{CATEGORIAS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Ubicación</Label><Input value={formData.ubicacion} onChange={e => setFormData({...formData, ubicacion: e.target.value})} placeholder="Estante A-1" /></div>
              <div><Label>Stock Mínimo</Label><Input type="number" value={formData.stock_minimo} onChange={e => setFormData({...formData, stock_minimo: parseInt(e.target.value) || 0})} /></div>
              <div><Label>Stock Actual</Label><Input type="number" value={formData.stock_actual} onChange={e => setFormData({...formData, stock_actual: parseInt(e.target.value) || 0})} /></div>
              <div><Label>Precio Unitario</Label><Input type="number" value={formData.precio_unitario} onChange={e => setFormData({...formData, precio_unitario: parseFloat(e.target.value) || 0})} /></div>
              <div className="flex items-center gap-2 pt-6"><Switch checked={formData.requiere_receta} onCheckedChange={v => setFormData({...formData, requiere_receta: v})} /><Label>Requiere Receta</Label></div>
              <div className="col-span-2"><Label>Notas</Label><Textarea value={formData.notas} onChange={e => setFormData({...formData, notas: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={saving} className="bg-[#0C314C] text-white">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
